// Control change reconciliation. Pure. No React, no Three.js.
//
// When a unit's controller changes mid-game (Betrayal, Infiltrate,
// Mesmerism, or the manual "switch ownership" menu action), almost all
// of its state travels with it untouched: tap state, damage, equipment,
// statuses, and position persist. A control change is not a re-summon.
// Ownership-relative auras re-segregate on the next static-aura recompute
// (they key off `card.rotated`, which the flip already updated). The one
// piece of derived state the recompute does NOT touch, and that the base
// summoning-sickness model gets wrong on a control change, is reconciled
// here.
//
// ── Codex anchor. Summoning Sickness ──
//   "A minion that entered the realm this turn... suffers from summoning
//   sickness until the end of turn." Sickness is scoped to the TURN the
//   minion entered, not to its controller.
//
// core/summoningSickness.js clears the flag at the start of the
// CONTROLLER's next turn (the untap-all step only walks the active
// player's minions). That shortcut is correct as long as control never
// changes. A minion only acts for the player who summoned it, and by
// that player's next untap the flag is already gone.
//
// A control change breaks the shortcut. A minion the opponent summoned on
// their previous turn still carries `_hasSummoningSickness` during your
// turn (your untap step skipped it. It wasn't yours yet). Taking control
// of it now would inherit that stale flag and wrongly forbid you from
// tapping it to move or attack, even though the turn it entered has ended
// and the codex says it is no longer sick.

import { hasSummoningSickness, clearSummoningSickness } from './summoningSickness';

/**
 * Canonical key for a single game turn: the active player plus their
 * per-player turn counter. Stamped on a minion when it enters the realm
 * (`_summonedOnTurnKey`) and compared at control-change time.
 */
export function turnKey(activePlayer, turnNumbers) {
  return `${activePlayer}:${(turnNumbers && turnNumbers[activePlayer]) || 0}`;
}

/**
 * Reconcile a unit's transient state after its controller changes.
 *
 * Clears stale summoning sickness. Kept ONLY when the minion genuinely
 * entered the realm during the current turn (the rare "opponent's trigger
 * summons on your turn, you steal it the same turn" case, still sick per
 * the codex).
 *
 * @param {Object} card - the card whose control just changed.
 * @param {string} currentTurnKey - turnKey() for the turn of the change.
 * @returns {{ clearedSickness: boolean }} so the host rebuilds badges /
 *   logs only when something actually changed.
 */
export function reconcileControlChange(card, currentTurnKey) {
  const result = { clearedSickness: false };
  if (!card) return result;
  if (hasSummoningSickness(card) && card._summonedOnTurnKey !== currentTurnKey) {
    clearSummoningSickness(card);
    result.clearedSickness = true;
  }
  return result;
}
